"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ipChangeoverParamsSchema = exports.getIpChangeoverQuerySchema = exports.applyIpChangeoverSchema = exports.IP_REFUND_MODES = exports.cancelIpChangeoverSchema = exports.rejectIpChangeoverSchema = exports.createIpChangeoverSchema = exports.ipChangeoverStatusEnum = exports.ipChangeoverDirectionEnum = void 0;
const zod_1 = require("zod");
/**
 * IP PAYER CHANGEOVER — change who pays for an existing stay.
 *
 * No money field appears in any request: every figure a changeover carries is
 * computed by the server's analyzer from the stay's own charges, the target
 * policy and its pre-authorisation, and shown back for approval. A client can
 * name WHO the stay moves to and WHY — never what it costs.
 */
exports.ipChangeoverDirectionEnum = zod_1.z.enum(['SELF_PAY_TO_INSURANCE', 'INSURANCE_TO_SELF_PAY']);
exports.ipChangeoverStatusEnum = zod_1.z.enum([
    'DRAFT',
    'ANALYZING',
    'ANALYZED',
    'BLOCKED',
    'AWAITING_APPROVAL',
    'APPROVED',
    'APPLYING',
    'COMPLETED',
    'FAILED',
    'REJECTED',
    'CANCELLED',
]);
exports.createIpChangeoverSchema = zod_1.z
    .object({
    direction: exports.ipChangeoverDirectionEnum,
    /** The policy the stay moves TO. Required for SELF_PAY_TO_INSURANCE, forbidden for the reverse. */
    newPatientCoverageId: zod_1.z.string().uuid().optional(),
    /** Why — a short code or phrase the desk can report on. */
    reasonCode: zod_1.z.string().trim().min(1, 'A reason is required.').max(100),
    remarks: zod_1.z.string().trim().max(1000).optional(),
})
    .superRefine((value, ctx) => {
    if (value.direction === 'SELF_PAY_TO_INSURANCE' && !value.newPatientCoverageId) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['newPatientCoverageId'], message: 'Name the insurance policy the stay moves to.' });
    }
    if (value.direction === 'INSURANCE_TO_SELF_PAY' && value.newPatientCoverageId) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['newPatientCoverageId'], message: 'A move to self-pay has no target policy.' });
    }
});
/** Mandatory: a rejection or a cancellation with no reason cannot be audited. */
exports.rejectIpChangeoverSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(3, 'A reason is required.').max(500),
});
exports.cancelIpChangeoverSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(3, 'A reason is required.').max(500),
});
/**
 * The ways a refund can be paid — the SAME list the desk's own Refund screen offers, so an excess
 * deposit returned by a changeover is indistinguishable from one returned by hand.
 */
exports.IP_REFUND_MODES = ['Cash', 'Card', 'UPI', 'Cheque', 'Bank transfer'];
/**
 * APPLY. Empty for an ordinary changeover. Only when insurance leaves the patient's deposits larger
 * than what they now owe does the desk say HOW that excess is to be refunded — through the refund
 * process, exactly as a manual refund: a voucher, and (as on the Refund screen) the bank and
 * reference for anything but cash. The server works out the AMOUNT; no figure is accepted here.
 * Supplying a mode also requires `ip:refund:create`, checked by the route.
 */
exports.applyIpChangeoverSchema = zod_1.z
    .object({
    refundMode: zod_1.z.enum(exports.IP_REFUND_MODES).optional(),
    refundBank: zod_1.z.string().trim().max(100).optional(),
    refundReference: zod_1.z.string().trim().max(100).optional(),
    refundRemarks: zod_1.z.string().trim().max(500).optional(),
})
    .superRefine((value, ctx) => {
    if (!value.refundMode) {
        for (const k of ['refundBank', 'refundReference', 'refundRemarks']) {
            if (value[k])
                ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: [k], message: 'Choose a refund mode first.' });
        }
        return;
    }
    if (value.refundMode !== 'Cash') {
        if (!value.refundBank)
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['refundBank'], message: `The bank is required for ${value.refundMode}.` });
        if (!value.refundReference)
            ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['refundReference'], message: `The reference number is required for ${value.refundMode}.` });
    }
});
exports.getIpChangeoverQuerySchema = zod_1.z.object({
    /** Which analysis to show; defaults to the latest. Every earlier version is kept. */
    analysisVersion: zod_1.z.coerce.number().int().min(1).optional(),
});
exports.ipChangeoverParamsSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    changeoverId: zod_1.z.string().uuid(),
});
//# sourceMappingURL=ip-changeover.schema.js.map